import { AraClient } from './client';
import { generateId } from './utils';
// Cross SDK will be loaded from CDN or window.CrossSdk at runtime
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let CrossSdk: any = null;

// CROSS SDK singleton (shared across widgets)
let crossSdkInitialized = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let crossSdkInstance: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let appkitWallet: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let crossAccountState: any = null;
let crossNetworkState: any = null;

async function ensureCrossSdk() {
  if (crossSdkInitialized) return;
  if (!CrossSdk) {
    // Try global first (if user added a script tag), else load from CDN
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = typeof window !== 'undefined' ? (window as any) : null;
    if (w && w.CrossSdk) {
      CrossSdk = w.CrossSdk;
    } else if (typeof window !== 'undefined') {
      // Avoid TS module resolution by using eval'd dynamic import
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      const dynamicImport = new Function('u', 'return import(u)') as (u: string) => Promise<any>;
      CrossSdk = await dynamicImport('https://sdk-cdn.crosstoken.io/cross-sdk/1.8.1/cross-sdk.js');
    }
  }
  const metadata = {
    name: 'ARA Embed',
    description: 'ARA Chat + CROSS Wallet',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    icons: ['https://contents.crosstoken.io/wallet/token/images/CROSSx.svg'],
  };
  const projectId = (typeof window !== 'undefined' && (window as any).VITE_PROJECT_ID) || '0979fd7c92ec3dbd8e78f433c3e5a523';
  const redirectUrl = typeof window !== 'undefined' ? window.location.href : '';
  if ((CrossSdk as any).initCrossSdk) {
    crossSdkInstance = (CrossSdk as any).initCrossSdk(
      projectId,
      redirectUrl,
      metadata,
      'light',
      undefined,
      undefined,
      (CrossSdk as any).ConstantsUtil?.getUniversalLink?.()
    );
  } else {
    crossSdkInstance = (CrossSdk as any).initCrossSdkWithParams({ projectId, redirectUrl, metadata, themeMode: 'light' });
  }
  appkitWallet = (CrossSdk as any).useAppKitWallet();
  crossSdkInstance.subscribeAccount((state: unknown) => {
    crossAccountState = state as any;
  });
  if (crossSdkInstance.subscribeNetwork) {
    crossSdkInstance.subscribeNetwork((state: unknown) => {
      crossNetworkState = state as any;
    });
  }
  crossSdkInitialized = true;
}

export type ButtonTheme = {
  backgroundColor?: string;
  right?: number;
  bottom?: number;
  size?: number; // px
  iconColor?: string;
  customIconSrc?: string;
  autoWindowOpen?: { autoOpen: boolean; openDelay?: number; autoOpenOnMobile?: boolean };
};

export type MessageTheme = {
  backgroundColor?: string;
  textColor?: string;
  showAvatar?: boolean;
  avatarSrc?: string;
};

export type TextInputTheme = {
  placeholder?: string;
  backgroundColor?: string;
  textColor?: string;
  sendButtonColor?: string;
};

export type ChatWindowTheme = {
  showTitle?: boolean;
  title?: string;
  titleAvatarSrc?: string;
  titleBackgroundColor?: string;
  titleTextColor?: string;
  welcomeMessage?: string;
  errorMessage?: string;
  backgroundColor?: string;
  height?: number;
  width?: number;
  fontSize?: number;
  botMessage?: MessageTheme;
  userMessage?: MessageTheme;
  textInput?: TextInputTheme;
};

export type Theme = {
  button?: ButtonTheme;
  chatWindow?: ChatWindowTheme;
};

export type InitOptions = {
  baseUrl: string;
  bearerToken?: string;
  chatId?: string;
  locale?: string;
  requestType?: string;
  walletAddress?: string;
  container?: HTMLElement | string; // for initFull only
  theme?: Theme;
};

type SendHandler = (q: string) => Promise<void>;

function createBubble(theme: MessageTheme | undefined, align: 'left' | 'right', html: string) {
  const wrap = document.createElement('div');
  wrap.style.margin = '8px 0';
  wrap.style.textAlign = align === 'left' ? 'left' : 'right';
  const span = document.createElement('span');
  span.style.display = 'inline-block';
  span.style.padding = '8px 12px';
  span.style.borderRadius = align === 'left' ? '12px 12px 12px 4px' : '12px 12px 4px 12px';
  span.style.maxWidth = '80%';
  span.style.wordBreak = 'break-word';
  span.style.background = theme?.backgroundColor ?? (align === 'left' ? '#f3f4f6' : '#111827');
  span.style.color = theme?.textColor ?? (align === 'left' ? '#111827' : '#fff');
  span.innerHTML = html;
  wrap.appendChild(span);
  return wrap;
}

function attachCoreChat(container: HTMLElement, opts: InitOptions, client: AraClient) {
  const theme = opts.theme?.chatWindow ?? {};

  const root = document.createElement('div');
  root.style.position = 'relative';
  root.style.display = 'flex';
  root.style.flexDirection = 'column';
  root.style.background = theme.backgroundColor ?? '#fff';
  root.style.width = (theme.width ?? 380) + 'px';
  root.style.height = (theme.height ?? 560) + 'px';
  root.style.border = '1px solid #e5e7eb';
  root.style.borderRadius = '14px';
  root.style.boxShadow = '0 12px 30px rgba(0,0,0,0.12)';
  root.style.overflow = 'hidden';
  root.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial';
  root.style.fontSize = (theme.fontSize ?? 14) + 'px';

  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.alignItems = 'center';
  header.style.gap = '8px';
  header.style.padding = '10px 12px';
  header.style.background = theme.titleBackgroundColor ?? '#111827';
  header.style.color = theme.titleTextColor ?? '#fff';
  header.style.position = 'relative';
  if (theme.showTitle !== false) {
    if (theme.titleAvatarSrc) {
      const img = document.createElement('img');
      img.src = theme.titleAvatarSrc;
      img.alt = 'avatar';
      img.width = 20;
      img.height = 20;
      img.style.borderRadius = '50%';
      header.appendChild(img);
    }
    const title = document.createElement('div');
    title.textContent = theme.title ?? 'ARA';
    title.style.fontWeight = '600';
    header.appendChild(title);
  }

  // Add action area (top-right "+" button)
  const headerActions = document.createElement('div');
  headerActions.style.marginLeft = 'auto';
  headerActions.style.display = 'flex';
  headerActions.style.alignItems = 'center';
  headerActions.style.gap = '6px';

  const plusBtn = document.createElement('button');
  plusBtn.type = 'button';
  plusBtn.setAttribute('aria-label', 'Open transfer widget');
  plusBtn.textContent = '+';
  plusBtn.style.width = '24px';
  plusBtn.style.height = '24px';
  plusBtn.style.lineHeight = '24px';
  plusBtn.style.borderRadius = '6px';
  plusBtn.style.border = '1px solid rgba(255,255,255,0.2)';
  plusBtn.style.background = 'transparent';
  plusBtn.style.color = header.style.color;
  plusBtn.style.cursor = 'pointer';
  plusBtn.style.fontWeight = '700';
  plusBtn.style.fontSize = '16px';

  headerActions.appendChild(plusBtn);
  header.appendChild(headerActions);

  const view = document.createElement('div');
  view.style.flex = '1';
  view.style.padding = '12px';
  view.style.overflow = 'auto';

  const inputWrap = document.createElement('div');
  inputWrap.style.display = 'flex';
  inputWrap.style.gap = '8px';
  inputWrap.style.padding = '10px 12px';
  inputWrap.style.borderTop = '1px solid #e5e7eb';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = opts.theme?.chatWindow?.textInput?.placeholder ?? '메시지를 입력하세요';
  input.style.flex = '1';
  input.style.border = '1px solid #e5e7eb';
  input.style.borderRadius = '10px';
  input.style.padding = '10px 12px';
  input.style.background = opts.theme?.chatWindow?.textInput?.backgroundColor ?? '#fff';
  input.style.color = opts.theme?.chatWindow?.textInput?.textColor ?? '#111827';

  const send = document.createElement('button');
  send.textContent = '전송';
  send.type = 'button';
  send.style.border = '1px solid #e5e7eb';
  send.style.borderRadius = '10px';
  send.style.padding = '10px 12px';
  send.style.background = opts.theme?.chatWindow?.textInput?.sendButtonColor ?? '#111827';
  send.style.color = '#fff';

  inputWrap.appendChild(input);
  inputWrap.appendChild(send);

  root.appendChild(header);
  root.appendChild(view);
  root.appendChild(inputWrap);
  container.appendChild(root);

  // Transfer widget shell (overlay inside chat window)
  const widgetShell = document.createElement('div');
  widgetShell.style.position = 'absolute';
  widgetShell.style.top = '50px';
  widgetShell.style.right = '12px';
  widgetShell.style.zIndex = '10';
  widgetShell.style.display = 'none';
  root.appendChild(widgetShell);

  const chatId = opts.chatId ?? generateId(12);
  if (theme.welcomeMessage) {
    view.appendChild(createBubble(theme.botMessage, 'left', escapeHtml(theme.welcomeMessage)));
  }

  let currentAnswer: HTMLDivElement | null = null;

  function begin() {
    currentAnswer = createBubble(theme.botMessage, 'left', '');
    view.appendChild(currentAnswer);
    view.scrollTop = view.scrollHeight;
  }

  function item(text: string) {
    if (!currentAnswer) return;
    const span = currentAnswer.querySelector('span');
    if (!span) return;
    span.textContent = (span.textContent ?? '') + text;
    view.scrollTop = view.scrollHeight;
  }

  function end() {
    currentAnswer = null;
  }

  function onError(err: unknown) {
    const msg = theme.errorMessage ?? '오류가 발생했습니다';
    view.appendChild(createBubble(theme.botMessage, 'left', `${msg}: ${escapeHtml(String(err))}`));
  }

  async function doSend(q: string) {
    if (!q) return;
    view.appendChild(createBubble(theme.userMessage, 'right', escapeHtml(q)));
    input.value = '';
    await client.start(
      {
        body: {
          chatId,
          question: q,
          locale: opts.locale ?? (navigator.language || 'ko-KR'),
          requestType: opts.requestType,
          walletAddress: opts.walletAddress,
        },
      },
      { onBegin: () => begin(), onItem: (t) => item(t), onEnd: () => end(), onError }
    );
  }

  send.addEventListener('click', () => doSend(input.value.trim()));
  let composing = false;
  input.addEventListener('compositionstart', () => {
    composing = true;
  });
  input.addEventListener('compositionend', () => {
    composing = false;
  });
  input.addEventListener('keydown', (e) => {
    // IME 조합(한글 등) 중 Enter 입력은 무시하여 중복 전송 방지
    // 일부 브라우저는 조합 중 keyCode 229를 사용
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyEvent = e as any;
    if (composing || (e as KeyboardEvent).isComposing || anyEvent.keyCode === 229) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      doSend(input.value.trim());
    }
  });

  // Mount simple CROSS transfer widget
  let widgetCreated = false;
  let widgetOpen = false;
  let setFromAddress: ((addr: string | null) => void) | null = null;
  let updateBusy: ((busy: boolean, text?: string) => void) | null = null;

  function ensureWidget() {
    if (widgetCreated) return;
    const controls = createCrossTransferWidget(widgetShell);
    setFromAddress = controls.setFromAddress;
    updateBusy = controls.updateBusy;
    widgetCreated = true;
  }

  // CROSS SDK singleton (lazy init per page)
  let crossSdkInitialized = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let crossSdkInstance: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let appkitWallet: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let crossAccountState: any = null;

  async function ensureCrossSdkLocal() {
    if (crossSdkInitialized) return;
    // 전역 CrossSdk 보장: window.CrossSdk 또는 CDN에서 동적 로드
    if (!CrossSdk) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w: any = typeof window !== 'undefined' ? (window as any) : null;
      if (w && w.CrossSdk) {
        CrossSdk = w.CrossSdk;
      } else if (typeof window !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-implied-eval
        const dynamicImport = new Function('u', 'return import(u)') as (u: string) => Promise<any>;
        CrossSdk = await dynamicImport('https://sdk-cdn.crosstoken.io/cross-sdk/1.8.1/cross-sdk.js');
      }
    }
    const metadata = {
      name: 'ARA Embed',
      description: 'ARA Chat + CROSS Wallet',
      url: typeof window !== 'undefined' ? window.location.origin : '',
      icons: ['https://contents.crosstoken.io/wallet/token/images/CROSSx.svg'],
    };
    const projectId = (typeof window !== 'undefined' && (window as any).VITE_PROJECT_ID) || '0979fd7c92ec3dbd8e78f433c3e5a523';
    const redirectUrl = typeof window !== 'undefined' ? window.location.href : '';
    crossSdkInstance = (CrossSdk as any).initCrossSdkWithParams({ projectId, redirectUrl, metadata, themeMode: 'light' });
    appkitWallet = (CrossSdk as any).useAppKitWallet();
    crossSdkInstance.subscribeAccount((state: unknown) => {
      crossAccountState = state as any;
      // Update widget header shortcut if opened
      setFromAddress?.((crossAccountState?.address as string) ?? null);
    });
    crossSdkInitialized = true;
  }

  plusBtn.addEventListener('click', async () => {
    ensureWidget();
    widgetOpen = !widgetOpen;
    widgetShell.style.display = widgetOpen ? 'block' : 'none';
    if (!widgetOpen) return;
    await ensureCrossSdkLocal();
    // reflect latest account
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const addr = (crossAccountState?.address as any) ?? null;
    setFromAddress?.(addr ?? null);
  });

  return { root, doSend } as const;
}

export function initPopup(options: InitOptions) {
  const theme = options.theme?.button ?? {};

  const client = new AraClient({ baseUrl: options.baseUrl, bearerToken: options.bearerToken });

  const btn = document.createElement('button');
  const size = theme.size ?? 56;
  btn.style.position = 'fixed';
  btn.style.right = (theme.right ?? 20) + 'px';
  btn.style.bottom = (theme.bottom ?? 20) + 'px';
  btn.style.width = size + 'px';
  btn.style.height = size + 'px';
  btn.style.borderRadius = '50%';
  btn.style.border = 'none';
  btn.style.boxShadow = '0 10px 20px rgba(0,0,0,0.15)';
  btn.style.cursor = 'pointer';
  btn.style.background = theme.backgroundColor ?? '#111827';
  btn.style.color = theme.iconColor ?? '#fff';
  btn.style.zIndex = '2147483000';
  btn.setAttribute('aria-label', 'Open ARA chat');
  if (theme.customIconSrc) {
    btn.style.backgroundImage = `url(${theme.customIconSrc})`;
    btn.style.backgroundSize = '60%';
    btn.style.backgroundRepeat = 'no-repeat';
    btn.style.backgroundPosition = 'center';
  } else {
    btn.innerHTML = 'ARA';
    btn.style.fontWeight = '700';
  }

  const shell = document.createElement('div');
  shell.style.position = 'fixed';
  shell.style.right = ((theme.right ?? 20) + 4) + 'px';
  shell.style.bottom = ((theme.bottom ?? 20) + (size + 10)) + 'px';
  shell.style.zIndex = '2147483000';
  shell.style.display = 'none';

  const { root } = attachCoreChat(shell, options, client);

  document.body.appendChild(btn);
  document.body.appendChild(shell);

  let open = false;
  function toggle() {
    open = !open;
    shell.style.display = open ? 'block' : 'none';
  }

  btn.addEventListener('click', toggle);

  if (theme.autoWindowOpen?.autoOpen) {
    const delay = Math.max(0, (theme.autoWindowOpen.openDelay ?? 0)) * 1000;
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    if (!isMobile || theme.autoWindowOpen.autoOpenOnMobile) {
      setTimeout(() => {
        toggle();
      }, delay);
    }
  }

  return {
    open: () => {
      if (!open) toggle();
    },
    close: () => {
      if (open) toggle();
    },
    destroy: () => {
      document.body.removeChild(btn);
      document.body.removeChild(shell);
    },
  } as const;
}

export function initFull(options: InitOptions) {
  const client = new AraClient({ baseUrl: options.baseUrl, bearerToken: options.bearerToken });
  const container =
    typeof options.container === 'string'
      ? (document.querySelector(options.container) as HTMLElement | null)
      : options.container ?? null;

  const mount = container ?? (() => {
    const el = document.createElement('div');
    el.style.position = 'fixed';
    el.style.left = '0';
    el.style.top = '0';
    el.style.width = '100vw';
    el.style.height = '100vh';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.background = options.theme?.chatWindow?.backgroundColor ?? '#f9fafb';
    document.body.appendChild(el);
    return el;
  })();

  const { root } = attachCoreChat(mount, options, client);
  if (!container) {
    // center dialog style
    root.style.width = (options.theme?.chatWindow?.width ?? 560) + 'px';
    root.style.height = (options.theme?.chatWindow?.height ?? 640) + 'px';
  } else {
    // fill container
    root.style.width = '100%';
    root.style.height = '100%';
    root.style.borderRadius = '0';
  }

  return {
    destroy: () => {
      if (!container && mount.parentElement) {
        mount.parentElement.removeChild(mount);
      } else if (container) {
        mount.innerHTML = '';
      }
    },
  } as const;
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Lightweight CROSS transfer widget (inside chat)
function createCrossTransferWidget(mount: HTMLElement) {
  const style = document.createElement('style');
  style.textContent = `
  .ara-xfer-panel { background:#1c2029; width:335px; border-radius:16px; padding:16px; color:#fff; box-shadow:0 10px 24px rgba(0,0,0,.28); }
  .ara-xfer-title { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
  .ara-xfer-sub { color:#20e2bb; font-size:14px; font-weight:600; }
  .ara-xfer-field { background:rgba(0,0,0,.3); border-radius:12px; padding:10px 12px; display:flex; align-items:center; gap:8px; }
  .ara-xfer-label { font-size:12px; color:#fff; margin:6px 0 6px 2px; }
  .ara-xfer-input { flex:1; background:transparent; border:none; outline:none; color:#fff; font-size:14px; }
  .ara-xfer-meta { color:#9b8cef; font-size:12px; margin-top:6px; }
  .ara-xfer-row { display:flex; flex-direction:column; gap:6px; margin-bottom:10px; }
  .ara-xfer-button { width:100%; height:40px; border-radius:12px; background:#998aef; border:none; color:#000; font-size:15px; font-weight:600; cursor:pointer; }
  .ara-xfer-button[disabled] { opacity:.6; cursor:not-allowed; }
  .ara-xfer-from { display:flex; align-items:center; gap:8px; }
  .ara-xfer-connect { margin-left:auto; background:transparent; color:#20e2bb; border:1px solid rgba(32,226,187,.4); padding:6px 10px; border-radius:8px; cursor:pointer; }
  .ara-xfer-toast { margin-top:8px; font-size:12px; color:#20e2bb; min-height:16px; }
  `;
  mount.appendChild(style);

  const root = document.createElement('div');
  root.className = 'ara-xfer-panel';

  const title = document.createElement('div');
  title.className = 'ara-xfer-title';
  title.innerHTML = `<div class="ara-xfer-sub">Asset Transfer</div>`;
  root.appendChild(title);

  // From (wallet)
  const fromRow = document.createElement('div');
  fromRow.className = 'ara-xfer-row';
  const fromLabel = document.createElement('div');
  fromLabel.className = 'ara-xfer-label';
  fromLabel.textContent = 'From';
  const fromField = document.createElement('div');
  fromField.className = 'ara-xfer-field ara-xfer-from';
  const fromText = document.createElement('div');
  fromText.style.fontSize = '14px';
  fromText.textContent = '지갑이 연결되지 않았습니다';
  const btnConnect = document.createElement('button');
  btnConnect.className = 'ara-xfer-connect';
  btnConnect.type = 'button';
  btnConnect.textContent = 'Connect';
  fromField.appendChild(fromText);
  fromField.appendChild(btnConnect);
  fromRow.appendChild(fromLabel);
  fromRow.appendChild(fromField);
  root.appendChild(fromRow);

  // To
  const toRow = document.createElement('div');
  toRow.className = 'ara-xfer-row';
  const toLabel = document.createElement('div');
  toLabel.className = 'ara-xfer-label';
  toLabel.textContent = 'To';
  const toField = document.createElement('div');
  toField.className = 'ara-xfer-field';
  const toInput = document.createElement('input');
  toInput.className = 'ara-xfer-input';
  toInput.placeholder = 'Address (0x...) 또는 이름';
  toField.appendChild(toInput);
  toRow.appendChild(toLabel);
  toRow.appendChild(toField);
  root.appendChild(toRow);

  // Amount
  const amtRow = document.createElement('div');
  amtRow.className = 'ara-xfer-row';
  const amtLabel = document.createElement('div');
  amtLabel.className = 'ara-xfer-label';
  amtLabel.textContent = 'Amount (CROSS)';
  const amtField = document.createElement('div');
  amtField.className = 'ara-xfer-field';
  const amtInput = document.createElement('input');
  amtInput.className = 'ara-xfer-input';
  amtInput.placeholder = '전송할 수량 입력';
  amtInput.inputMode = 'decimal';
  amtField.appendChild(amtInput);
  const balanceMeta = document.createElement('div');
  balanceMeta.className = 'ara-xfer-meta';
  balanceMeta.textContent = 'Balance: -';
  amtRow.appendChild(amtLabel);
  amtRow.appendChild(amtField);
  amtRow.appendChild(balanceMeta);
  root.appendChild(amtRow);

  const btnSend = document.createElement('button');
  btnSend.className = 'ara-xfer-button';
  btnSend.type = 'button';
  btnSend.textContent = 'Transfer';
  root.appendChild(btnSend);

  const toast = document.createElement('div');
  toast.className = 'ara-xfer-toast';
  root.appendChild(toast);

  mount.appendChild(root);

  // State
  let currentAccount: string | null = null;

  function updateFrom(addr: string | null) {
    currentAccount = addr;
    if (addr) {
      const short = addr.slice(0, 6) + '...' + addr.slice(-4);
      fromText.textContent = short;
      btnConnect.textContent = 'Connected';
      btnConnect.disabled = true;
    } else {
      fromText.textContent = '지갑이 연결되지 않았습니다';
      btnConnect.textContent = 'Connect';
      btnConnect.disabled = false;
    }
  }

  function setBusy(busy: boolean, text?: string) {
    btnSend.disabled = busy;
    btnConnect.disabled = btnConnect.disabled || busy;
    toast.textContent = text ?? '';
  }

  function parseEtherToHexWei(v: string): string {
    const s = v.trim();
    if (!/^[0-9]+(\.[0-9]{0,18})?$/.test(s)) throw new Error('잘못된 금액 형식');
    const [intPart, fracPart = ''] = s.split('.');
    const frac = (fracPart + '0'.repeat(18)).slice(0, 18);
    const wei = BigInt(intPart) * 10n ** 18n + BigInt(frac);
    return '0x' + wei.toString(16);
  }

  // Initialize CROSS SDK and subscribe to account updates
  ensureCrossSdk().then(() => {
    try {
      crossSdkInstance?.subscribeAccount((state: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s = state as any;
        const addr = (s?.address as string) ?? null;
        updateFrom(addr);
        const b = (s?.balance as string | number | bigint | undefined);
        if (b !== undefined && b !== null) {
          const text = typeof b === 'bigint' ? b.toString() : String(b);
          balanceMeta.textContent = `Balance: ${text}`;
        }
      });
    } catch {}
  });

  async function connect() {
    await ensureCrossSdk();
    try {
      await appkitWallet.connect('cross_wallet');
    } catch (e) {
      toast.textContent = '연결이 취소되었거나 실패했습니다';
    }
  }

  async function send() {
    await ensureCrossSdk();
    if (!currentAccount) {
      await connect();
      if (!currentAccount) return;
    }
    const to = toInput.value.trim();
    const amt = amtInput.value.trim();
    if (!to) {
      toast.textContent = '수신 주소를 입력하세요';
      return;
    }
    if (!/^0x[0-9a-fA-F]{40}$/.test(to)) {
      toast.textContent = '올바른 EVM 주소가 아닙니다';
      return;
    }
    try {
      setBusy(true, '전송 중...');
      const amount = (() => {
        const s = amt.trim();
        if (!/^[0-9]+(\.[0-9]{0,18})?$/.test(s)) throw new Error('잘못된 금액 형식');
        return parseFloat(s || '0');
      })();
      const resTx = await (CrossSdk as any).SendController.sendNativeToken({
        data: '0x',
        receiverAddress: to,
        sendTokenAmount: amount,
        decimals: '18',
        type: (CrossSdk as any).ConstantsUtil.TRANSACTION_TYPE.LEGACY,
      });
      const done = typeof resTx === 'string' ? resTx : (resTx?.hash ?? 'done');
      setBusy(false, `전송 완료: ${String(done).slice(0, 18)}...`);
    } catch (e) {
      setBusy(false, `전송 실패: ${String(e)}`);
    }
  }

  btnConnect.addEventListener('click', connect);
  btnSend.addEventListener('click', send);

  return {
    setFromAddress(addr: string | null) {
      updateFrom(addr);
    },
    updateBusy: setBusy,
  } as const;
}


